import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import { toast } from "sonner";
import { Menu, Bell, Sparkles, LogOut, User, Gift, HelpCircle, BookOpen } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ProductTutorial from "./ProductTutorial";

export default function Header({ user, usage, onToggleSidebar }) {
  const navigate = useNavigate();
  const { signOut: clerkSignOut } = useClerk();
  const [showTutorial, setShowTutorial] = useState(false);
  const [notificationsRead, setNotificationsRead] = useState(() => {
    return localStorage.getItem("clientkit_notifications_read") === "true";
  });
  const usagePercent = usage ? (usage.gen_count_month / usage.gen_cap_month) * 100 : 0;
  const isTrial = usage?.is_trial && !usage?.is_demo;
  const trialDaysLeft = usage?.trial_days_left;
  const isDemo = usage?.is_demo;
  const demoStatus = usage?.demo_status;
  
  // Check if there are unread notifications
  const hasUnreadNotifications = !notificationsRead || (usage && usage.remaining <= 10) || (isTrial && trialDaysLeft && trialDaysLeft <= 3);
  
  const handleNotificationsOpen = (open) => {
    if (open) {
      setNotificationsRead(true);
      localStorage.setItem("clientkit_notifications_read", "true");
    }
  };

  // Format demo time remaining
  const formatDemoTime = (seconds) => {
    if (!seconds || seconds <= 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLogout = async () => {
    try {
      if (window.Clerk?.session) {
        await clerkSignOut();
      }
      toast.success("Logged out successfully");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/", { replace: true });
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };
  
  return (
    <header className="h-14 lg:h-16 border-b border-slate-700/50 bg-slate-900/30 backdrop-blur-sm px-3 sm:px-4 lg:px-6 flex items-center justify-between">
      <div className="flex items-center gap-2 sm:gap-4">
        <button
          onClick={onToggleSidebar}
          className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          data-testid="toggle-sidebar-btn"
        >
          <Menu className="w-5 h-5 text-indigo-400" />
        </button>
        
        <div className="hidden sm:block">
          <h2 className="text-sm font-medium text-slate-400 truncate max-w-[150px] lg:max-w-none">
            Welcome, {user?.name?.split(' ')[0] || "User"}
          </h2>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        {/* Demo Banner - Takes priority over Trial */}
        {isDemo && demoStatus && (
          <div className={`hidden xl:flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
            demoStatus.is_locked 
              ? 'bg-red-500/20 border-red-500/30' 
              : 'bg-indigo-500/20 border-indigo-500/30'
          }`}>
            <Sparkles className={`w-4 h-4 ${demoStatus.is_locked ? 'text-red-400' : 'text-indigo-400'}`} />
            <span className={`text-xs ${demoStatus.is_locked ? 'text-red-300' : 'text-indigo-400'}`}>
              {demoStatus.is_locked ? (
                <span>Demo ended</span>
              ) : (
                <>
                  <strong>{formatDemoTime(demoStatus.time_remaining_seconds)}</strong> • {demoStatus.premium_uses_remaining} features left
                </>
              )}
            </span>
          </div>
        )}

        {/* Trial Banner - Only show if not demo */}
        {!isDemo && isTrial && trialDaysLeft !== null && (
          <div className="hidden xl:flex items-center gap-2 bg-gradient-to-r from-indigo-500/20 to-indigo-500/20 px-3 py-1.5 rounded-lg border border-indigo-500/30">
            <Gift className="w-4 h-4 text-indigo-400" />
            <span className="text-xs text-indigo-400">
              <strong>{trialDaysLeft}</strong> days left
            </span>
          </div>
        )}

        {/* Free-tier credit pill (Plan 05-03) — brick-red when exhausted; hidden for paid/demo */}
        {user?.tier === "free" && !isDemo && typeof usage?.llm_credits_remaining === "number" && (
          <button
            onClick={() => navigate("/billing")}
            className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
              usage.llm_credits_remaining > 0
                ? "bg-slate-800/50 border-slate-700/50 text-indigo-400 hover:bg-slate-800"
                : "bg-[#B23A48]/15 border-[#B23A48]/40 text-[#E07A86] hover:bg-[#B23A48]/25"
            }`}
            data-testid="credit-pill"
          >
            <Sparkles className="w-4 h-4" />
            {usage.llm_credits_remaining > 0
              ? `${usage.llm_credits_remaining}/3 free responses`
              : "0/3 — Upgrade for unlimited"}
          </button>
        )}

        {/* Usage Indicator */}
        {usage && (
          <div className="hidden lg:flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-xl border border-slate-700/50">
            <Sparkles className="w-4 h-4 text-indigo-400" />
            <div className="w-24">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-indigo-400">Usage</span>
                <span className="text-slate-400">{usage.gen_count_month}/{usage.gen_cap_month}</span>
              </div>
              <Progress value={usagePercent} className="h-1.5" />
            </div>
          </div>
        )}

        {/* Tier Badge */}
        {user && (
          <Badge 
            variant={user.tier === "free" ? "secondary" : "default"}
            className={`hidden sm:inline-flex text-xs ${
              user.tier === "pro" 
                ? "bg-indigo-500/20 text-indigo-400 border-indigo-500/30" 
                : user.tier === "growth"
                ? "bg-indigo-500/20 text-indigo-400 border-indigo-500/30"
                : "bg-slate-400 text-slate-400"
            } ${isTrial ? "animate-pulse" : ""}`}
            data-testid="tier-badge"
          >
            {isTrial ? "Trial" : user.tier ? user.tier.charAt(0).toUpperCase() + user.tier.slice(1) : "Free"}
          </Badge>
        )}

        {/* Notifications */}
        <DropdownMenu onOpenChange={handleNotificationsOpen}>
          <DropdownMenuTrigger asChild>
            <button className="p-2 hover:bg-slate-800 rounded-lg transition-colors relative" data-testid="notifications-btn">
              <Bell className="w-5 h-5 text-indigo-400" />
              {hasUnreadNotifications && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-indigo-500 rounded-full"></span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 bg-slate-800 border-slate-700">
            <div className="px-3 py-2 border-b border-slate-700">
              <p className="font-medium text-indigo-500">Notifications</p>
            </div>
            <div className="py-2">
              <div className="px-3 py-2 text-sm text-indigo-400 hover:bg-slate-400/50 cursor-pointer">
                <p className="text-slate-600">Welcome to ClientKit! 🎉</p>
                <p className="text-xs text-slate-400 mt-0.5">Get started with your first client reply</p>
              </div>
              {isTrial && trialDaysLeft && (
                <div className="px-3 py-2 text-sm text-indigo-400 hover:bg-slate-400/50 cursor-pointer">
                  <p className="text-indigo-400">Trial ending soon</p>
                  <p className="text-xs text-slate-400 mt-0.5">{trialDaysLeft} days left on your Pro trial</p>
                </div>
              )}
              {usage && usage.remaining <= 10 && (
                <div className="px-3 py-2 text-sm text-indigo-400 hover:bg-slate-400/50 cursor-pointer">
                  <p className="text-orange-400">Usage alert</p>
                  <p className="text-xs text-slate-400 mt-0.5">{usage.remaining} generations remaining this month</p>
                </div>
              )}
            </div>
            <div className="px-3 py-2 border-t border-slate-700 text-center">
              <p className="text-xs text-slate-400">You&apos;re all caught up!</p>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Help / Tutorial Button */}
        <button 
          onClick={() => setShowTutorial(true)}
          className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          title="Product Tour"
          data-testid="help-btn"
        >
          <HelpCircle className="w-5 h-5 text-indigo-400" />
        </button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 hover:bg-slate-800 rounded-xl p-1 pr-3 transition-colors">
              <Avatar className="w-8 h-8">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-indigo-500/20 text-indigo-400 text-sm">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-slate-400 hidden lg:block">{user?.name?.split(" ")[0]}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700">
            <div className="px-2 py-2">
              <p className="text-sm font-medium text-indigo-500">{user?.name}</p>
              <p className="text-xs text-indigo-400">{user?.email}</p>
              {isTrial && (
                <p className="text-xs text-indigo-400 mt-1">Pro Trial • {trialDaysLeft} days left</p>
              )}
            </div>
            <DropdownMenuSeparator className="bg-slate-400" />
            <DropdownMenuItem 
              className="text-slate-400 focus:text-indigo-500 focus:bg-slate-400 cursor-pointer"
              onClick={() => navigate("/billing")}
            >
              <User className="w-4 h-4 mr-2" />
              Account & Billing
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="text-slate-400 focus:text-indigo-500 focus:bg-slate-400 cursor-pointer"
              onClick={() => setShowTutorial(true)}
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Product Tour
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-slate-400" />
            <DropdownMenuItem 
              className="text-red-400 focus:text-red-300 focus:bg-red-500/10 cursor-pointer"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Product Tutorial Modal */}
        <ProductTutorial 
          isOpen={showTutorial} 
          onClose={() => setShowTutorial(false)}
          onComplete={() => {
            localStorage.setItem("clientkit_tutorial_completed", "true");
          }}
        />
      </div>
    </header>
  );
}
